import { Box } from '@mui/material';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import { GoogleMap, Marker, Circle, useJsApiLoader } from '@react-google-maps/api';
import { getConfig } from 'config';
import { useMemo } from 'react';

interface CostCenterMiniMapProps {
  latitude?: number;
  longitude?: number;
  radiusMeters?: number;
}

const isValidCoordinate = (lat?: number, lng?: number): boolean =>
  typeof lat === 'number' &&
  typeof lng === 'number' &&
  !Number.isNaN(lat) &&
  !Number.isNaN(lng) &&
  lat >= -90 &&
  lat <= 90 &&
  lng >= -180 &&
  lng <= 180;

const CostCenterMiniMap = ({ latitude, longitude, radiusMeters }: CostCenterMiniMapProps): JSX.Element => {
  const { isLoaded } = useJsApiLoader({
    id: 'cost-center-map-loader',
    googleMapsApiKey: getConfig('GOOGLE_MAPS_API_KEY') ?? '',
  });

  const hasCoordinates = isValidCoordinate(latitude, longitude);
  const center = useMemo(() => ({ lat: latitude as number, lng: longitude as number }), [latitude, longitude]);

  if (!hasCoordinates || !isLoaded) {
    return (
      <Box
        sx={{
          height: 240,
          width: '100%',
          background: '#f3f3f3',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <MapOutlinedIcon sx={{ fontSize: 48, color: '#bdbdbd' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ height: 240, width: '100%', borderRadius: '8px', overflow: 'hidden' }}>
      <GoogleMap
        mapContainerStyle={{ height: '100%', width: '100%' }}
        center={center}
        zoom={15}
        options={{ disableDefaultUI: true, zoomControl: true, scrollwheel: false }}
      >
        <Marker position={center} />
        {typeof radiusMeters === 'number' && radiusMeters > 0 && (
          <Circle
            center={center}
            radius={radiusMeters}
            options={{
              fillColor: '#1976d2',
              fillOpacity: 0.12,
              strokeColor: '#1976d2',
              strokeOpacity: 0.6,
              strokeWeight: 1,
            }}
          />
        )}
      </GoogleMap>
    </Box>
  );
};

CostCenterMiniMap.defaultProps = {
  latitude: undefined,
  longitude: undefined,
  radiusMeters: undefined,
};

export default CostCenterMiniMap;
